import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { type ReactNode, useState } from 'react';
import type { CreativeDetailTab } from '../../../lib/creative-detail-search';
import type { DetailCreative } from '../../creatives/data/api';
import { CreativeDetailDrawer } from '../../creatives/detail/CreativeDetailDrawer';
import { CommonTags } from './CommonTags';
import { type CardStat, CreativeCard } from './CreativeCard';
import { CreativeCarousel } from './CreativeCarousel';
import {
  type CreativeCard as CreativeCardData,
  type PerformanceReport,
  type ReportRequest,
  usePerformanceReport,
} from './data/api';
import { formatKpi, percent } from './data/format';
import { KpiTiles } from './KpiTiles';
import { ReportHeader } from './ReportHeader';

const ALERT_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

interface OpenedCreative {
  creativeId: string;
  tab: CreativeDetailTab;
}

/** Every creative the report lists, in page order, so the drawer can step between them. */
function listedCreatives(report: PerformanceReport): DetailCreative[] {
  const byCreative = new Map<string, CreativeCardData>();
  for (const card of [...report.top, ...report.bottom, ...report.fatigued, ...report.hookScore]) {
    if (!byCreative.has(card.creativeId)) byCreative.set(card.creativeId, card);
  }
  return [...byCreative.values()].map((card) => ({
    creativeId: card.creativeId,
    thumbnailUrl: card.thumbnailUrl ?? '',
    previewUrl: card.previewUrl ?? '',
    duration: card.duration,
    format: null,
    network: null,
    creativeType: card.creativeType,
    spendRatio: card.spendRatio,
    activeDays: card.activeDays,
    firstSeen: null,
    lastSeen: null,
    metrics: {},
    uplift: {},
  }));
}

export function PerformanceReportView({ request }: { request: ReportRequest }): ReactNode {
  const { data: report, isPending, error } = usePerformanceReport(request);
  const [opened, setOpened] = useState<OpenedCreative | null>(null);
  const handleOpen = (creativeId: string): void => setOpened({ creativeId, tab: 'overview' });

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (isPending || report === undefined) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { kpi, currencySymbol } = report;
  const kpiStat = (card: CreativeCardData, label = `${kpi.label} · primary KPI`): CardStat => ({
    label,
    value: formatKpi(card.kpiValue, kpi, currencySymbol),
  });
  const spendStat = (card: CreativeCardData): CardStat => ({
    label: 'Spend %',
    value: percent(card.spendRatio),
  });
  const daysStat = (card: CreativeCardData): CardStat => ({
    label: 'Days active',
    value: String(card.activeDays),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 10, pb: 12 }}>
      <ReportHeader report={report} accountCount={request.accountIds.length} />
      <KpiTiles report={report} />

      <CreativeCarousel
        title={`Top ${report.top.length} Performing Creatives`}
        description="Highlights the creatives with the strongest performance based on the leading KPI."
        items={report.top}
        itemKey={(card) => card.creativeId}
        empty="No creative clears the account average by enough to count as a top performer."
        renderItem={(card) => (
          <CreativeCard
            creative={card}
            status="top"
            primary={kpiStat(card)}
            stats={[spendStat(card), daysStat(card)]}
            onOpen={handleOpen}
          />
        )}
      />

      <CommonTags
        title="Common Tags - Top Performers"
        description="Shows the most frequently used tags within your top-performing creatives."
        tags={report.topTags}
      />

      <CreativeCarousel
        title="Underperforming Creatives"
        description="Lists creatives performing below the account average for the selected KPI."
        items={report.bottom}
        itemKey={(card) => card.creativeId}
        empty="No creative trails the account average by enough to count as underperforming."
        renderItem={(card) => (
          <CreativeCard
            creative={card}
            status="under"
            primary={kpiStat(card)}
            stats={[spendStat(card), daysStat(card)]}
            onOpen={handleOpen}
          />
        )}
      />

      <CommonTags
        title="Common Tags - Underperforming Creatives"
        description="Displays tags that frequently appear in low-performing creatives."
        tags={report.bottomTags}
      />

      <CreativeCarousel
        title="Fatigued Creatives"
        description="Creatives whose CTR has dropped enough over recent days to signal audience fatigue."
        items={report.fatigued}
        itemKey={(card) => card.creativeId}
        empty="No creative is fatigued right now."
        renderItem={(card) => (
          <CreativeCard
            creative={card}
            status="fatigued"
            hideDelta
            meta={`Alert trigger · ${ALERT_DATE_FORMAT.format(new Date(card.alertDate))}`}
            primary={{ label: 'Fatigue drop', value: percent(Math.abs(card.decline), 0) }}
            stats={[{ label: 'CTR', value: percent(card.ctr) }, spendStat(card), daysStat(card)]}
            onOpen={handleOpen}
          />
        )}
      />

      <CreativeCarousel
        title="Top Creatives by Hook Score"
        description="Surfaces the creatives with the strongest hook in the first few seconds."
        items={report.hookScore}
        itemKey={(card) => card.creativeId}
        empty="No creative has a hook score for this period."
        renderItem={(card) => (
          <CreativeCard
            creative={card}
            status="hook"
            hideDelta
            primary={{ label: 'Hook score', value: String(Math.round(card.hookScore)) }}
            stats={[kpiStat(card, kpi.label), daysStat(card)]}
            onOpen={handleOpen}
          />
        )}
      />

      {opened !== null && (
        <CreativeDetailDrawer
          creatives={listedCreatives(report)}
          creativeId={opened.creativeId}
          tab={opened.tab}
          scope={{ dateRange: report.dateRange, accountIds: request.accountIds }}
          kpis={[kpi.name]}
          leadingKpiLabel={kpi.label}
          onOpen={handleOpen}
          onTabChange={(tab) => setOpened({ ...opened, tab })}
          onClose={() => setOpened(null)}
        />
      )}
    </Box>
  );
}
